<template>
  <div>
    <h4>GitHub Repositories</h4>
    <b-tabs
      active-tab-class="active-tab"
      align="left"
      content-class="mt-3"
    >
      <b-tab
        title="Active"
      >
        <b-row class="my-1">
          <b-col
            sm="4"
            md="4"
          >
            <b-form-group
              label="Repo Name"
              label-for="filter-input"
              label-cols-sm="4"
              label-align-sm="center"
              label-size="sm"
            >
              <b-input-group size="sm">
                <b-form-input
                  id="filter-input"
                  v-model="filter"
                  type="search"
                  placeholder="Type to Search"
                />

                <b-input-group-append>
                  <b-button
                    :disabled="!filter"
                    @click="filter = ''"
                  >
                    Clear
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col
            sm="4"
            md="4"
          >
            <b-form-group
              label="Per page"
              label-for="per-page-select"
              label-cols-sm="6"
              label-cols-md="4"
              label-cols-lg="3"
              label-align-sm="right"
              label-size="sm"
              class="mb-0"
            >
              <b-form-select
                id="per-page-select"
                v-model="perPage"
                :options="pageOptions"
                size="sm"
              />
            </b-form-group>
          </b-col>
          <b-col
            sm="4"
            md="4"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="repolen"
              :per-page="perPage"
              size="sm"
              align="right"
              class="my-0"
            />
          </b-col>
        </b-row>
        <b-table
          :items="repositories"
          :fields="fields"
          :current-page="currentPage"
          :per-page="perPage"
          :filter="filter"
          :filter-included-fields="filterOn"
          :busy="loadingResources"
        >
          <template #table-busy>
            <div>
              <b-spinner class="align-middle" />
              <strong>Loading...</strong>
            </div>
          </template>
          <template #cell(url)="row">
            <a
              :href="row.value"
              target="_blank"
            >
              {{ row.value }}
            </a>
          </template>
          <template #cell(actions)="row">
            <b-button @click="edit(row.item)">
              <font-awesome-icon icon="edit" />
            </b-button>

            <b-button
              ref="archivePromptShow"
              v-b-modal.archivePrompt
              title="Archive"
              variant="danger"
              class="mr-2"
              @click.stop="currentRepository = row.item"
            >
              <font-awesome-icon icon="trash-alt" />
            </b-button>
          </template>
        </b-table>
      </b-tab>
      <b-tab
        title="Archived"
      >
        <b-table
          :items="archivedRepositories"
          :fields="fields"
          :busy="loadingResources"
        >
          <template #table-busy>
            <div>
              <b-spinner class="align-middle" />
              <strong>Loading...</strong>
            </div>
          </template>
          <template #cell(url)="row">
            <a
              :href="row.value"
              target="_blank"
            >
              {{ row.value }}
            </a>
          </template>
        </b-table>
      </b-tab>
    </b-tabs>

    <b-modal
      id="update-repository-modal"
      hide-header
      hide-footer
    >
      <h5>Updating "{{ currentRepository.name }}"</h5>
      <b-form-group
        id="repository-name-group"
        description="Characters that don't match the regex [A-Za-z0-9_.-] are turned into dashes."
        :valid-feedback="validFeedback"
        :invalid-feedback="invalidFeedback"
        :state="state"
        label="Name: "
        label-cols="auto"
        content-cols="auto"
      >
        <b-form-input
          id="name-input"
          v-model="nameInput"
          :state="state"
          style="width: 430px; margin: 0 auto"
        />
      </b-form-group>
      <b-button
        variant="success"
        :disabled="inProgress"
        @click="update"
      >
        Update
      </b-button>
    </b-modal>
    <b-modal
      id="archivePrompt"
      header-bg-variant="danger"
      header-text-variant="light"
      centered
      title="Archive Repository"
      @ok="archive"
    >
      <b-container fluid>
        {{ `Are you sure you want to archive` }}
        <strong>{{ currentRepository.name }}</strong> ?
        <h6>
          Type the name of the repository to confirm.
          If you want to unarchive a repository, you must submit a
          <a href="https://publicengines.atlassian.net/servicedesk/customer/portal/6/group/10/create/201">ticket</a>
          to DLT so that a repo admin can do it via the Github UI. It cannot be done here.
        </h6>
        <b-input v-model="confirmRepoName" />
      </b-container>
      <div slot="modal-footer">
        <b-button
          class
          @click="hideModal"
        >
          Cancel
        </b-button>
        <b-button
          class
          variant="primary"
          :disabled="confirmRepoName !== currentRepository.name"
          @click="archive"
        >
          Ok
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import api from '@/api';
import {getRepositoryName} from '../utils/github';
const defaultCurrentRepository = {name: 'None Selected'};

export default {
  props: {
    project: {
      type: Object,
      required: true,
      validator: (value) => !!value.projectName,
    },
    repositories: {
      type: Array,
      required: true,
    },
    archivedRepositories: {
      type: Array,
      required: true,
    },
    loadingResources: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      confirmRepoName: '',
      currentRepository: defaultCurrentRepository,
      inProgress: false,
      nameInput: '',
      currentPage: 1,
      perPage: 10,
      pageOptions: [5, 10, 15, 20],
      filter: null,
      filterOn: ['name'],
      updateDetails: {},
      fields: [
        {key: 'name', label: 'Name'},
        {key: 'url', label: 'Link'},
        {key: 'actions', label: 'Actions'},
      ],
    };
  },
  computed: {
    repolen() {
      return this.repositories.length;
    },
    state() {
      return this.isValidUpdate();
    },
    invalidFeedback() {
      return 'Must be a unique non-empty name that isn\'t being used in this project already.';
    },
    validFeedback() {
      const name = getRepositoryName(this.nameInput);
      return `The repository's name will be: ${this.project.projectName}_${name}`;
    },
  },
  methods: {
    edit(repository) {
      this.currentRepository = repository;
      this.$bvModal.show('update-repository-modal');
    },
    async update() {
      if (!this.inProgress && this.isValidUpdate()) {
        this.inProgress = true;
        const updates = {
          name: `${this.project.projectName}_${this.nameInput}`,
        };
        await api.updateGithubRepository(this.currentRepository.name, updates);
        this.$bvModal.hide('update-repository-modal');
        this.$emit('updated-repository', {
          updatedRepository: this.currentRepository,
          updates,
        });
        this.currentRepository = defaultCurrentRepository;
        this.nameInput = '';
        this.inProgress = false;
      }
    },
    isValidUpdate() {
      for (const repository of this.repositories) {
        if (repository.name.split('_')[1] === getRepositoryName(this.nameInput)) {
          return false;
        }
      }
      return this.nameInput.length >= 1;
    },
    async archive() {
      const name = this.currentRepository.name;
      if (name) {
        await api.archiveGithubRepository(name);
      }
      this.$emit('archived-repository', {name});
      this.hideModal();
    },
    hideModal() {
      this.confirmRepoName = '';
      this.$root.$emit('bv::hide::modal', 'archivePrompt');
    },
  },
};
</script>
